/* The "Out of the Menus" Part-1 action mapping (designs/navigation-redesign.md
   NR3): pure decision logic for the record heading — which transition is the
   one natural next step (the primary button), which verb is the quieter
   second, which transitions demote to the ⋯ overflow, and how the pipeline
   stepper reads. Projects and portfolio items share the four-state pipeline
   (section-navigator N6), so one mapping serves both; the ONLY differences
   are vocabulary — a project approval commits proposed allocations ("Approve
   & commit"), an item's commits nothing ("Approve"); a project steps "Back to
   draft", an item "Move[s] to draft" — exactly the File menu's own words.
   The `to`/`verb` pairs are the File ops' verbatim; the store actions that
   consume them are unchanged (NR4). */
import { RECORD_STATES, type RecordState } from "./projects";

/* one File-menu verb, relocated: label for the button, `to` for the store
   action, `verb` for setRecordState's failure copy (items ignore it) */
export interface RecordTransition {
  label: string;
  to: RecordState;
  verb: string;
}

export type RecordKind = "project" | "item";

export type SecondaryAction =
  | { kind: "edit"; label: "Edit" }
  | ({ kind: "transition" } & RecordTransition);

const toDraft = (kind: RecordKind): RecordTransition => ({
  label: kind === "project" ? "Back to draft" : "Move to draft",
  to: "draft",
  verb: "move",
});

const ARCHIVE: RecordTransition = { label: "Archive", to: "archived", verb: "archive" };

/* The one natural next step per state (NR3): forward while the pipeline goes
   forward, the closing move for active, the way back for archived. */
export function primaryAction(state: RecordState, kind: RecordKind): RecordTransition {
  switch (state) {
    case "draft":
      return { label: "Submit for review →", to: "review", verb: "submit" };
    case "review":
      return {
        label: kind === "project" ? "Approve & commit →" : "Approve →",
        to: "active",
        verb: "approve",
      };
    case "active":
      return { label: "Archive →", to: "archived", verb: "archive" };
    case "archived":
      return { label: "Unarchive →", to: "active", verb: "unarchive" };
  }
}

/* The quieter second button: review steps back to draft (the artifact's
   mockup), every other state edits the record. */
export function secondaryAction(state: RecordState, kind: RecordKind): SecondaryAction {
  return state === "review"
    ? { kind: "transition", ...toDraft(kind) }
    : { kind: "edit", label: "Edit" };
}

/* What remains for the overflow: the state's File-menu transitions minus the
   two promoted above — the same shared verb list, just demoted (NR5). */
export function overflowTransitions(state: RecordState, kind: RecordKind): RecordTransition[] {
  switch (state) {
    case "draft":
    case "review":
      return [ARCHIVE];
    case "active":
      return [toDraft(kind)];
    case "archived":
      return [];
  }
}

/* The stepper: the canonical pipeline order with each step's standing. The
   walk is ordinal — a step before the current one reads as passed. */
export interface PipelineStep {
  state: RecordState;
  status: "done" | "current" | "todo";
}

export function pipelineSteps(state: RecordState): PipelineStep[] {
  const at = RECORD_STATES.indexOf(state);
  return RECORD_STATES.map((s, i) => ({
    state: s,
    status: i < at ? "done" : i === at ? "current" : "todo",
  }));
}
